const invalidFileNameCharacters = /[\u0000-\u001f\u007f]/g;

const toWellFormedUnicode = (value: unknown): string =>
  Array.from(String(value ?? ''), (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return character.length === 1 && codePoint >= 0xd800 && codePoint <= 0xdfff
      ? '\uFFFD'
      : character;
  }).join('');

export const sanitizeDownloadFileName = (value: unknown, fallback = 'download'): string => {
  const normalized = toWellFormedUnicode(value).normalize('NFC');
  const leafName = normalized.split(/[\\/]/).pop() || '';
  const sanitized = leafName.replace(invalidFileNameCharacters, ' ').trim();
  if (sanitized) return sanitized;
  if (fallback === value) return 'download';
  return sanitizeDownloadFileName(fallback, 'download');
};

const decodeExtendedFileName = (value: string): string | null => {
  const unquoted = value.trim().replace(/^"|"$/g, '');
  const extended = unquoted.match(/^([^']*)'[^']*'(.*)$/);
  if (!extended || extended[1].toLowerCase() !== 'utf-8') return null;
  try {
    return decodeURIComponent(extended[2]);
  } catch {
    return null;
  }
};

const decodeLegacyPercentEncoding = (value: string): string => {
  if (!/%[0-9a-f]{2}/i.test(value)) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const filenameFromContentDisposition = (
  disposition: unknown,
  fallback = 'download'
): string => {
  const header = String(disposition ?? '');
  const extendedMatch = header.match(/filename\*\s*=\s*(?:"([^"]*)"|([^;\s]*))/i);
  const extendedValue = extendedMatch?.[1] ?? extendedMatch?.[2];
  if (extendedValue) {
    const decoded = decodeExtendedFileName(extendedValue);
    if (decoded) return sanitizeDownloadFileName(decoded, fallback);
  }

  const plainMatch = header.match(/filename\s*=\s*(?:"((?:\\.|[^"])*)"|([^;]*))/i);
  const plainValue = plainMatch?.[1]?.replace(/\\([\\"])/g, '$1') ?? plainMatch?.[2]?.trim();
  if (plainValue) {
    return sanitizeDownloadFileName(decodeLegacyPercentEncoding(plainValue), fallback);
  }
  return sanitizeDownloadFileName(fallback, 'download');
};
